import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { FunctionComponent, SVGProps } from 'react';
import {
  LogoShieldIcon,
  CaretIcon,
  UNavEngagementsIcon,
  UNavProjectsIcon,
  UNavDirectionsIcon,
  UNavInvitationsIcon,
  UNavCitizenIcon,
  UNavProgressIcon,
  UNavReportsIcon,
  UNavLearningIcon,
} from '../../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
interface NavItem {
  to: string;
  label: string;
  Icon: IconType;
}

const TOP_ITEMS: NavItem[] = [
  { to: '/user/engagements', label: 'Engagements', Icon: UNavEngagementsIcon },
  { to: '/user/projects', label: 'Projects', Icon: UNavProjectsIcon },
  { to: '/user/directions', label: 'Daily Directions', Icon: UNavDirectionsIcon },
];
const BOTTOM_ITEMS: NavItem[] = [
  { to: '/user/progress', label: 'Progress Dashboard', Icon: UNavProgressIcon },
  { to: '/user/reports', label: 'Reports', Icon: UNavReportsIcon },
  { to: '/user/learning', label: 'Learning Center', Icon: UNavLearningIcon },
];

const CITIZEN_TO = '/user/citizen-requests';
const INVITATION_LEAVES = [
  { to: `${CITIZEN_TO}/wedding`, label: 'Wedding' },
  { to: `${CITIZEN_TO}/shoksabha`, label: 'Shoksabha' },
  { to: `${CITIZEN_TO}/events`, label: 'Events' },
  { to: `${CITIZEN_TO}/media`, label: 'Media' },
];

const linkCls = ({ isActive }: { isActive: boolean }) =>
  [
    'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium no-underline transition-colors',
    isActive ? 'bg-brand-shade text-brand' : 'text-ink-secondary hover:bg-brand-shade/60 hover:text-brand',
  ].join(' ');

interface UserSidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export default function UserSidebar({ open = false, onClose }: UserSidebarProps) {
  const { pathname } = useLocation();
  const citizenSection = pathname.startsWith(CITIZEN_TO);
  const leafActive = INVITATION_LEAVES.some((l) => pathname.startsWith(l.to));
  // Both groups toggle open/closed like a dropdown; they start open when in-section.
  const [crOpen, setCrOpen] = useState(citizenSection || leafActive);
  const [invOpen, setInvOpen] = useState(leafActive || citizenSection);

  return (
    <>
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 top-16 z-20 bg-black/30 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={[
          'fixed left-0 top-16 z-30 flex h-[calc(100vh-64px)] w-[280px] flex-col gap-6 overflow-y-auto border-r border-line-soft bg-white pl-4 pr-[17px] py-4 transition-transform duration-200 lg:z-10 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Admin profile card */}
        <div className="flex items-center gap-3 rounded-xl bg-brand-shade p-4">
          <span className="flex items-center justify-center rounded-lg bg-brand p-2 text-white">
            <LogoShieldIcon className="size-5" />
          </span>
          <div className="flex flex-col">
            <span className="text-xs font-bold leading-4 text-ink">CHUNAAV Admin</span>
            <span className="text-[10px] font-medium uppercase leading-[15px] tracking-[0.5px] text-brand">
              System Administrator
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1">
          {TOP_ITEMS.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} onClick={onClose} className={linkCls}>
              <Icon className="size-5 shrink-0" />
              <span className="leading-5">{label}</span>
            </NavLink>
          ))}

          {/* Citizen Requests — with the nested Invitation group */}
          <NavLink
            to={CITIZEN_TO}
            end
            onClick={() => {
              setCrOpen((o) => !o);
              onClose?.();
            }}
            // Highlight for the whole section (incl. /new), except the Invitation leaves.
            className={linkCls({ isActive: citizenSection && !leafActive })}
          >
            <UNavCitizenIcon className="size-5 shrink-0" />
            <span className="leading-5">Citizen Requests</span>
            <CaretIcon
              className={['ml-auto size-4 shrink-0 transition-transform', crOpen ? '' : '-rotate-90'].join(' ')}
            />
          </NavLink>
          {crOpen && (
            <div className="ml-4 flex flex-col gap-1 border-l border-line-soft pl-2">
              <button
                type="button"
                onClick={() => setInvOpen((o) => !o)}
                className="flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade/60 hover:text-brand"
              >
                <UNavInvitationsIcon className="size-[18px] shrink-0" />
                <span className="leading-5">Invitation</span>
                <CaretIcon
                  className={['ml-auto size-4 shrink-0 transition-transform', invOpen ? '' : '-rotate-90'].join(' ')}
                />
              </button>
              {invOpen && (
                <div className="ml-4 flex flex-col gap-1 border-l border-line-soft pl-2">
                  {INVITATION_LEAVES.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={onClose}
                      className={({ isActive }) =>
                        [
                          'rounded-lg px-4 py-2 text-sm font-medium no-underline transition-colors',
                          isActive ? 'bg-brand-shade text-brand' : 'text-ink-secondary hover:bg-brand-shade/60 hover:text-brand',
                        ].join(' ')
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {BOTTOM_ITEMS.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} onClick={onClose} className={linkCls}>
              <Icon className="size-5 shrink-0" />
              <span className="leading-5">{label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}
